import type { RouteObject } from 'react-router-dom'
import { Navigate } from 'react-router-dom'
import { AppPaths } from '@/config/app-paths'
import { WorkspaceDeskLandingRedirect } from '@/app/navigation/workspace-desk-landing-redirect'
import { bossRoutes } from '@/features/boss/routes'
import { payrollRoutes } from '@/features/payroll/routes'
import { financeRoutes } from '@/features/finance/routes'
import { invoicingRoutes } from '@/features/invoicing/routes'
import { selfAttendanceRoutes } from '@/features/self-attendance/routes'
import { supervisorStaffDocsRoutes } from '@/features/supervisor-staff-docs/routes'
import { documentFilingRoutes } from '@/features/document-filing/routes'
import { clientRoutes } from '@/features/clients/routes'
import { accessControlRoutes } from '@/features/access-control/routes'
import { teamRoutes } from '@/features/team/routes'
import { leaveRoutes } from '@/features/leaves/routes'
import { stockRoutes } from '@/features/stocks/routes'
import { gatePassRoutes } from '@/features/gate-pass/routes'
import { analyticsRoutes } from '@/features/analytics/routes'
import { staffPersonalDetailsRoutes } from '@/features/staff-personal-details/routes'
import { interviewIntakeRoutes } from '@/features/interview-intake/routes'
import { contactInquiriesRoutes } from '@/features/contact-inquiries/routes'
import { proOnboardingRoutes } from '@/features/pro-onboarding/routes'
import { proExitRoutes } from '@/features/pro-exit/routes'
import { isSalesFeatureEnabled } from '@/features/sales/permissions'
import { salesRoutes } from '@/features/sales/routes'
import { PlaceholderPage } from '@/shared/pages/placeholder-page'

type StubRoute = {
  path: string
  title: string
  description: string
  routeNote?: string
}

const stub = (r: StubRoute) => ({
  path: r.path,
  element: (
    <PlaceholderPage
      title={r.title}
      description={r.description}
      routeNote={r.routeNote}
    />
  ),
})

/** Protected app routes (placeholders + implemented pages). */
export const protectedRouteChildren: RouteObject[] = [
  { index: true, element: <WorkspaceDeskLandingRedirect /> },
  {
    path: 'home',
    lazy: async () => ({
      Component: (await import('@/features/auth/pages/home-page')).HomePage,
    }),
  },
  {
    path: AppPaths.workforceDirectorySegment,
    lazy: async () => {
      const { WorkforceDirectoryPage } = await import(
        '@/features/workforce/pages/workforce-directory-page'
      )
      const { ProEmployeeDirectoryGate } = await import(
        '@/features/workforce/components/pro-employee-directory-gate'
      )
      return {
        Component: function GatedWorkforceDirectoryPage() {
          return (
            <ProEmployeeDirectoryGate>
              <WorkforceDirectoryPage />
            </ProEmployeeDirectoryGate>
          )
        },
      }
    },
  },
  {
    path: 'workforce/completeness',
    lazy: async () => ({
      Component: (await import('@/features/workforce/pages/workforce-completeness-page'))
        .WorkforceCompletenessPage,
    }),
  },

  // Recruitment
  {
    path: 'recruitment/jobs',
    lazy: async () => ({
      Component: (await import('@/features/recruitment/pages/job-posts-page')).JobPostsPage,
    }),
  },
  {
    path: 'recruitment/jobs/:jobId',
    lazy: async () => ({
      Component: (await import('@/features/recruitment/pages/job-post-detail-page'))
        .JobPostDetailPage,
    }),
  },
  {
    path: 'recruitment/job-wizard',
    lazy: async () => ({
      Component: (await import('@/features/recruitment/pages/job-post-wizard-page'))
        .JobPostWizardPage,
    }),
  },
  {
    path: 'recruitment/applicants',
    lazy: async () => ({
      Component: (await import('@/features/recruitment/pages/candidates-board-page'))
        .ApplicantsListPage,
    }),
  },
  {
    path: 'recruitment/applicants/:candidateId',
    lazy: async () => ({
      Component: (await import('@/features/recruitment/pages/candidate-detail-page'))
        .CandidateDetailPage,
    }),
  },
  {
    path: 'recruitment/talent-pool',
    lazy: async () => ({
      Component: (await import('@/features/recruitment/pages/candidates-board-page'))
        .TalentPoolPage,
    }),
  },
  {
    path: 'recruitment/talent-pool/:candidateId',
    lazy: async () => ({
      Component: (await import('@/features/recruitment/pages/candidate-detail-page'))
        .CandidateDetailPage,
    }),
  },

  // HR onboarding
  {
    path: 'hr/onboarding',
    lazy: async () => ({
      Component: (await import('@/features/hr/pages/hr-onboarding-hub-page')).HrOnboardingHubPage,
    }),
  },
  {
    path: 'hr/onboarding/start',
    lazy: async () => ({
      Component: (await import('@/features/hr/pages/start-onboarding-case-page'))
        .StartOnboardingCasePage,
    }),
  },
  {
    path: 'hr/onboarding/walk-in',
    lazy: async () => ({
      Component: (await import('@/features/hr/pages/reception-walk-in-intake-page'))
        .ReceptionWalkInIntakePage,
    }),
  },
  {
    path: 'hr/onboarding/cases/:caseId',
    lazy: async () => ({
      Component: (await import('@/features/hr/pages/hr-onboarding-case-page'))
        .HrOnboardingCasePage,
    }),
  },
  stub({
    path: 'hr/onboarding/joining-form/:caseId',
    title: 'Joining Form',
    description: 'Full-page joining form for one onboarding case.',
    routeNote: 'Dynamic route — case detail panel will link here when needed.',
  }),
  stub({
    path: 'hr/onboarding/pro-handover/:caseId',
    title: 'PRO Handover',
    description: 'Checklist before submitting the case to PRO.',
  }),
  {
    path: 'hr/onboarding/new-hire/:kind',
    lazy: async () => ({
      Component: (await import('@/features/hr/pages/manual-hire-intake-page'))
        .ManualHireIntakePage,
    }),
  },

  // Workforce
  {
    path: 'workforce/staffing',
    element: <Navigate to={AppPaths.adminTeam} replace />,
  },
  stub({
    path: 'workforce/employees/add',
    title: 'Add Employee',
    description: 'Manual roster row (same contract as Excel import).',
  }),
  {
    path: 'workforce/employees/:employeeId',
    lazy: async () => ({
      Component: (await import('@/features/workforce/pages/workforce-employee-detail-page'))
        .WorkforceEmployeeDetailPage,
    }),
  },

  // Boss overview (plain Administrator)
  ...bossRoutes,

  // Payroll (feature routes)
  ...payrollRoutes,

  // Finance ledger + client invoicing
  ...financeRoutes,
  ...invoicingRoutes,

  // Supervisor self-attendance review (standalone slice)
  ...selfAttendanceRoutes,

  // Supervisor staff documents portal
  ...supervisorStaffDocsRoutes,
  ...documentFilingRoutes,

  // Clients (commercial identity)
  ...clientRoutes,

  // Sales CRM desk (dev only — hidden on staging/production; see isSalesFeatureEnabled)
  ...(isSalesFeatureEnabled() ? salesRoutes : []),

  // Access control
  ...accessControlRoutes,

  // Team
  ...teamRoutes,

  // Leave management (admin)
  ...leaveRoutes,

  // Stocks / inventory (tenant-scoped)
  ...stockRoutes,
  ...gatePassRoutes,

  // Analytics (admin)
  ...analyticsRoutes,

  // Staff personal details intake (HR)
  ...staffPersonalDetailsRoutes,

  // Interview applications (onboarding desk)
  ...interviewIntakeRoutes,

  // Website contact inquiries (admin)
  ...contactInquiriesRoutes,

  // Admin
  stub({
    path: 'admin/recruitment-search',
    title: 'Recruitment Search',
    description: 'Cross-tenant candidate lookup for admins.',
  }),
  stub({
    path: 'admin/staff-operations',
    title: 'Staff Operations',
    description: 'Operational insights for platform administrators.',
  }),
  stub({
    path: 'admin/notification-rules',
    title: 'Notification Rules',
    description: 'Configure event rules, templates, and preferences.',
  }),
  {
    path: 'admin/purged-employees',
    lazy: async () => ({
      Component: (await import('@/features/workforce/pages/purged-employees-page'))
        .PurgedEmployeesPage,
    }),
  },

  // Client
  stub({
    path: 'client/applicants',
    title: 'My Applicants',
    description: 'Client portal view of shared applicants.',
  }),

  // PRO
  ...proOnboardingRoutes,
  ...proExitRoutes,

  // Coming soon
  stub({
    path: 'coming-soon/career-portal',
    title: 'Career Portal',
    description: 'Public careers experience (disabled in Flutter nav).',
  }),

  // Account
  {
    path: 'account/notifications',
    lazy: async () => ({
      Component: (
        await import('@/features/notifications/pages/notification-preferences-page')
      ).NotificationPreferencesPage,
    }),
  },
  {
    path: 'account/access',
    lazy: async () => ({
      Component: (await import('@/features/auth/pages/my-access-page')).MyAccessPage,
    }),
  },
  stub({
    path: 'account/help',
    title: 'User Guide',
    description: 'Staff help and task guides.',
  }),
]
